/* eslint-disable no-console */
/* React */
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useRouteMatch } from 'react-router-dom';

/* APIs */
import { useUpdateCommentMutation } from '../api/CommentApi';

/* Models */
import { canUserRatePostComment, Comment, FullComment, FullPost, getSort, reverseSort } from 'model';

/* State */
import { useSelector } from 'react-redux';
import { RootState } from '../App';
import { LoginState } from '../state/loginSlice';

/* Components */
import { AnswerRate, CommentLikeDislike } from './LikeDislike';
import { PostContentsToHTML } from './PostContentsToHTML';

/* Utils */
import { flattenTree } from '../util/flattenTree';

/* UI */
import { IonAlert } from '@ionic/react';
import classNames from 'classnames';
import { CommentDeleteState } from '../state/commentViewSlice';
import commentStyles from '../styles/comments.module.css';
import { AuthorHeaderSmall } from './AuthorHeaderSmall';
import { CommentMenu } from './CommentMenu';
import { IconButton } from './IconButton';
import styles from './NestedCommentItem.module.css';
import { IconArrowForward, IconChatbox, IconCheck, IconClose, IconEdit } from './QuirkIcons';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';

export function getCommentDepth(comments: FullComment[], id: Comment['_id'], depth: number = 0): number {
	for (const comment of comments) {
		if (comment._id === id) {
			return depth;
		}
		const childDepth = getCommentDepth(comment.fullChildren, id, depth + 1);
		if (childDepth !== -1) {
			return childDepth;
		}
	}
	return -1;
}

export const NestedCommentItem: React.FC<{
	commentState: CommentDeleteState;
	post: FullPost;
	comment: FullComment;
	levels: number;
	commentId?: Comment['_id'];
	expanded?: boolean;
	starredComments?: Record<string, number> | undefined;
	testId?: string;
}> = ({ post, comment, levels, commentState, commentId, expanded, starredComments, testId }) => {
	const [updateComment] = useUpdateCommentMutation();
	const [editedComment, setEditedComment] = useState('');
	const [opened, setOpened] = useState(expanded ?? false);
	const editRef = useRef<HTMLTextAreaElement>(null);
	const [editing, setEditing] = useState(false);
	const maxBeforeShowMore = 5;
	const state: LoginState = useSelector<RootState, LoginState>(state => state.login);
	const [showAlert, setShowAlert] = useState(false);

	const { closedForComments } = post;
	const match = useRouteMatch();
	const userid = state.user._id;
	// Find our base path
	const path = match.path.replace('show/:postid/:expanded?/:commentid?', '');
	const editable = userid === comment.author;
	const fullChildren =
		levels === 2
			? flattenTree([...comment.fullChildren]).sort(getSort('created'))
			: [...comment.fullChildren].sort(reverseSort(getSort('created')));

	const firstChildren = fullChildren.slice(0, maxBeforeShowMore);
	const remainingChildren = fullChildren.slice(maxBeforeShowMore);

	// Default editedComment to the comment's contents read from DB
	useEffect(() => {
		setEditedComment(comment?.comment || '');
	}, [comment, comment.comment]);

	useEffect(() => {
		setOpened(open => {
			return expanded === undefined ? open : (firstChildren.length < maxBeforeShowMore || expanded) ?? false;
		});
	}, [expanded, firstChildren.length]);

	if (levels < 1) {
		return null;
	}

	const MoreCommentsShow: React.FC = () => {
		if (editing) {
			return null;
		}
		return (
			<>
				{levels > 1 && remainingChildren.length > 0 && !opened && (
					<div className={classNames(styles.moreReplies, 'pointer')}>
						<>
							<span onClick={() => setOpened(!opened)}>{`${remainingChildren.length} more ${
								remainingChildren.length > 1 ? 'replies\u00A0' : 'reply\u00A0'
							}`}</span>
							<IconArrowForward size="small" />
						</>
					</div>
				)}
			</>
		);
	};

	const RevisionMessage: React.FC = () => {
		if (comment.commentModerationReason && !editing) {
			return (
				<div className={commentStyles.revisionMessage} onClick={() => setShowAlert(true)}>
					This comment is hidden. <span>See reason »</span>
				</div>
			);
		} else {
			return null;
		}
	};

	const getRevisionCommentStyle = () => {
		if (comment.commentModerationReason !== '' && (comment.author === userid || post.author === userid)) {
			return commentStyles.commentBoxRevision;
		} else {
			return commentStyles.commentBox;
		}
	};

	return (
		<>
			{((comment.commentModerationReason !== '' && (comment.author === userid || post.author === userid)) ||
				comment.commentModerationReason === '') && (
				<div
					className={classNames(
						commentStyles.commentItem,
						{
							[commentStyles.commentDeleting]:
								comment._id === commentState?.currentComment?.id &&
								commentState?.currentComment.isLoading
						},
						{
							[commentStyles.commentDeleted]:
								comment._id === commentState?.currentComment?.id &&
								commentState?.currentComment.isSuccess
						},
						{
							[commentStyles.commentError]:
								comment._id === commentState?.currentComment?.id && commentState?.currentComment.isError
						}
					)}
					data-testid={testId}
				>
					<div className={postStyles.postHeader}>
						<div className={commentStyles.commentName}>
							<AuthorHeaderSmall authoredItem={comment} testId={`nestedCommentAuthor-${comment._id}`} />
						</div>
						<div className={postStyles.postMenuContainer}>
							<CommentMenu
								comment={comment}
								post={post}
								editButton={
									!editing && editable && !closedForComments ? (
										<IconButton
											label="Edit"
											icon={<IconEdit />}
											onClick={() => {
												setEditedComment(comment.comment);
												setEditing(true);
												setTimeout(() => {
													const textarea = editRef?.current;
													if (textarea) {
														textarea.style.height = editRef?.current?.scrollHeight + 'px';
														const end = textarea.value?.length;
														textarea.setSelectionRange(end, end);
														textarea.focus();
													}
												}, 0);
											}}
										/>
									) : null
								}
							/>
						</div>
					</div>

					<div
						className={!editing ? getRevisionCommentStyle() : commentStyles.commentBox}
						id={comment._id}
						data-testid={`nestedCommentContent-${comment._id}`}
					>
						{!editing && <PostContentsToHTML>{editedComment}</PostContentsToHTML>}
						{editing && (
							<textarea
								value={editedComment}
								onChange={e => {
									if (e.currentTarget) {
										e.currentTarget.style.height = e.currentTarget.scrollHeight + 'px';
									}
									setEditedComment(e.currentTarget.value);
								}}
								ref={editRef}
								className={styles.editCommentInline}
							></textarea>
						)}
					</div>
					<RevisionMessage />
					<IonAlert
						header="Comment hidden"
						subHeader={'Reason: "' + comment.commentModerationReason + '"'}
						message={
							comment.author === userid
								? 'Edit your comment to restore it.'
								: 'The user has been asked to edit their comment'
						}
						buttons={[
							{
								text: 'OK',
								handler: () => {
									setShowAlert(false);
								}
							}
						]}
						isOpen={showAlert}
					></IonAlert>
					<footer className={styles.footer}>
						<div className={styles.leftColumn}>
							{!closedForComments && post.postType !== 'communityQuestion' && (
								<div className={commentStyles.commentIconContainer}>
									<NavLink
										onClick={() => levels > 1 && setOpened(!opened)}
										to={`${path}${post._id}/comment/reply/${comment._id}`}
										className="nounderline"
										style={{ pointerEvents: closedForComments ? 'none' : 'all' }}
									>
										<IconButton
											label={'Reply'}
											icon={<IconChatbox />}
											testId="nestedCommentReplyButton"
										/>
									</NavLink>
								</div>
							)}
							{editing && (
								<>
									<div
										className={commentStyles.commentIconContainer}
										onClick={() => {
											if (editable) {
												updateComment({ comment: { ...comment, comment: editedComment } });
											}
											setEditing(false);
										}}
									>
										<IconButton
											label="Save"
											className={postStyles.postFootericons}
											icon={<IconCheck />}
										/>
									</div>
									<div
										className={commentStyles.commentIconContainer}
										onClick={() => {
											setEditedComment(comment.comment);
											setEditing(false);
										}}
									>
										<IconButton
											label="Cancel"
											className={postStyles.postFootericons}
											icon={<IconClose />}
										/>
									</div>
								</>
							)}
						</div>
						<div>
							<div className={styles.actionIcons}>
								{canUserRatePostComment(state.user, comment) && (
									<>
										{userid !== comment.author ? (
											post.postType === 'communityQuestion' ? (
												<AnswerRate
													likeable={comment}
													totalStars={starredComments?.[comment._id]}
												/>
											) : (
												<CommentLikeDislike likeable={comment} />
											)
										) : null}
									</>
								)}
							</div>

							<div></div>
						</div>
					</footer>
					{firstChildren.length > 0 && (
						<div
							className={classNames({
								[commentStyles.commentItemChildrenOpened]: true
							})}
							data-testid={`NestedCommentChildren-${comment._id}`}
						>
							{firstChildren?.map((child, i) => {
								return (
									<div
										key={child._id}
										className={commentId !== undefined ? styles.rolledOut : styles.rollOut}
									>
										<NestedCommentItem
											commentState={commentState}
											key={child._id}
											post={post}
											comment={child}
											levels={levels - 1}
											commentId={commentId}
											expanded={
												commentId !== undefined
													? comment._id !== commentId &&
													  getCommentDepth(comment.fullChildren, commentId) >= 0
													: false
											}
											testId={child._id}
										/>
									</div>
								);
							})}
							<MoreCommentsShow />
						</div>
					)}
					{remainingChildren.length > 0 && (
						<div
							className={classNames({
								[opened
									? commentStyles.commentItemChildrenOpened
									: commentStyles.commentItemChildrenClosed]: true
							})}
						>
							{remainingChildren?.map((child, i) => {
								return (
									<div
										key={child._id}
										className={
											opened
												? commentId !== undefined
													? styles.rolledOut
													: styles.rollOut
												: styles.blank
										}
									>
										<NestedCommentItem
											commentState={commentState}
											key={child._id}
											post={post}
											comment={child}
											levels={levels - 1}
											commentId={commentId}
											expanded={
												commentId !== undefined
													? comment._id !== commentId &&
													  getCommentDepth(comment.fullChildren, commentId) >= 0
													: false
											}
											testId={child._id}
										/>
									</div>
								);
							})}

							<MoreCommentsShow />
						</div>
					)}
				</div>
			)}
		</>
	);
};
